import { useEffect } from "react";
import { FormComponent as FormComponent_ } from "./interfaces/base";
import RenderChildren from "./RenderChildren";
import styled from "@emotion/styled";
import "./Form.css";
import BackgroundVideoComponent from "./BackgroundVideoComponent";
import { useAppSelector } from "../../store/hooks";
import { getPageLocaleUrl } from "./utilities/helpers";
import React from "react";

const Form = styled("form")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

interface Props {
  id: number;
  uuid?: string | null;
  attributes: FormComponent_["attributes"];
  editor: JSX.Element;
  childElements: any;
  childWebpageComponentRelations: any;
}

declare var API: any;

export default function FormComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: Props) {
  const activePage = useAppSelector((state) => state.builder.pages.active);
  const pages = useAppSelector((state) => state.builder.pages.list);
  const website = useAppSelector((state) => state.builder.website);
  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );
  const hash = Date.now().toString();

  const handleSubmit = (e: MouseEvent) => {
    e.preventDefault();
    const parentEl = document.getElementById(id.toString());
    const hashElement =
      parentEl?.querySelector<HTMLInputElement>("[name=hash]");
    const formData = new FormData(parentEl as HTMLFormElement);

    // Add full page URL to form data
    if (website && activePage && pages && languages) {
      const currentPage = pages.find((page) => page.uuid === activePage);
      if (currentPage) {
        const pageUrl = getPageLocaleUrl(currentPage, languages);
        const fullPageUrl = website.name + pageUrl;
        formData.append("website_url", fullPageUrl);
      }
    } else {
      // Fallback: use window.location if Redux data is not available
      formData.append("website_url", window.location.href);
    }

    if (hashElement && hashElement.value === hash) {
      window
        // @ts-ignore
        .axios("/submitContactForm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data: formData,
        })
        .then((response: any) => {
          if (response.status === 200) {
            document.getElementById("form-success-message")!.style.display =
              "block";
          } else {
            alert(response.statusText);
          }
        })
        .catch((error: any) => {
          console.error(error);
        });
    }
  };

  // Note: Hash is now included as a hidden field in the form,
  // so we don't need to manually set it via setTimeout anymore

  const getElementAsync = (className) => {
    return new Promise((resolve) => {
      const getElement = () => {
        const elements = document.querySelectorAll(`form .${className}`);

        if (elements.length > 0) {
          resolve(elements);
        } else {
          requestAnimationFrame(getElement);
        }
      };

      getElement();
    });
  };

  const componentDidMount = async () => {
    // Find the button element, that has type=="submit"
    const buttonElements = (await getElementAsync(
      "builder-component__button"
    )) as NodeListOf<HTMLButtonElement>;

    (buttonElements as NodeListOf<HTMLButtonElement>).forEach((button) => {
      // Check if button.type === 'submit', then add eventListener
      if (button.type === "submit") {
        button.addEventListener("click", handleSubmit);
      }
    });

    return () => {
      (buttonElements as NodeListOf<HTMLButtonElement>).forEach((button) => {
        // Check if button.type === 'submit', then add eventListener
        if (button.type === "submit") {
          button.removeEventListener("click", handleSubmit);
        }
      });
    };
  };

  useEffect(() => {
    componentDidMount();
  }, [activePage]);

  // Get full page URL for hidden field
  const getFullPageUrl = () => {
    if (website && activePage && pages && languages) {
      const currentPage = pages.find((page) => page.uuid === activePage);
      if (currentPage) {
        const pageUrl = getPageLocaleUrl(currentPage, languages);
        return website.name + pageUrl;
      }
    }
    // Fallback to window.location
    return window.location.href;
  };

  const fullPageUrl = getFullPageUrl();

  return (
    <>
      <Form
        id={id ? id.toString() : ""}
        onSubmit={handleSubmit}
        action={attributes.action}
        data-style={attributes && attributes.styles}
        className={attributes && attributes.classes + " relative"}
      >
        {/* Hidden fields */}
        <input type="hidden" name="website_url" value={fullPageUrl} />
        <input type="hidden" name="hash" value={hash} />

        <BackgroundVideoComponent componentKey={uuid} />
        <RenderChildren
          key={id + "children"}
          childWebpageComponentRelations={childWebpageComponentRelations}
        />
      </Form>
      <div
        style={{
          color: "green",
          fontSize: "14px",
          marginTop: "5px",
          display: "none",
        }}
        id="form-success-message"
      >
        Sõnum saadetud.
      </div>
    </>
  );
}
